import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { Instagram } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const silkEase = [0.22, 1, 0.36, 1] as const;

const sectionVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: silkEase } },
};

/** Messenger glyph — lucide has no brand mark for it. */
function MessengerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.652V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111C24 4.974 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z" />
    </svg>
  );
}

const channels = [
  { name: "Instagram", Icon: Instagram, color: "#E1306C" },
  { name: "Messenger", Icon: MessengerIcon, color: "#0084FF" },
];

export function Channels() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const { language } = useLanguage();
  const t = translations[language].channels;

  return (
    <section className="pt-24 pb-12 px-6">
      <motion.div
        ref={sectionRef}
        className="max-w-3xl mx-auto text-center"
        variants={sectionVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.h2
          className="text-3xl md:text-4xl mb-5"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.heading}
        </motion.h2>

        <motion.p
          className="text-lg md:text-xl mb-4"
          style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
          variants={itemVariants}
        >
          {t.body}
        </motion.p>

        <motion.p
          className="text-base"
          style={{ color: "var(--gold)", fontFamily: "var(--font-body)" }}
          variants={itemVariants}
        >
          {t.subtext}
        </motion.p>

        <motion.div className="flex justify-center gap-4 mt-12" variants={itemVariants}>
          {channels.map(({ name, Icon, color }) => (
            <div
              key={name}
              className="flex items-center gap-3 px-5 py-3 rounded-2xl"
              style={{
                border: "1px solid var(--border)",
                background: "var(--surface-1)",
              }}
            >
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${color}1A`, color }}
              >
                <Icon className="w-5 h-5" />
              </span>
              <span
                className="text-base"
                style={{ color: "var(--cream)", fontFamily: "var(--font-body)" }}
              >
                {name}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
